import jsPDF from 'jspdf';
import { BRAND, rgb, fmtDate } from './shared';
import { LEGAL_CONTACT } from '../../pages/legal/policyContent';

const GSTIN_MISSING = '⚠ GSTIN not provided';
const DUMMY_GSTIN = new Set(['27ABCDE1234F1Z5', '27ABCDE1234F1Z0']);

const COMPANY = {
  name: 'DEVELOPMENT EXPRESS',
  addr: 'Karad, Satara, Maharashtra — 415110',
  phone: '+91-8408000084',
  email: 'contact@developmentexpress.in',
  gstin: (typeof process !== 'undefined' && process.env && process.env.REACT_APP_DE_GSTIN) || '',
  web: 'developmentexpress.in',
};

function displayGstin(value) {
  const v = String(value || '').trim().toUpperCase();
  if (!v || DUMMY_GSTIN.has(v)) return GSTIN_MISSING;
  return v;
}

export async function generateServiceAgreement(user = {}) {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  const W = 210;
  const margin = 18;
  const contentW = W - margin * 2;
  let y = 20;

  // Header background
  doc.setFillColor(...rgb(BRAND.gold));
  doc.rect(0, 0, W, 28, 'F');

  // Company name
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(10, 10, 10);
  doc.text(COMPANY.name, margin, 12);

  // Tagline
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(60, 60, 60);
  doc.text('Construction Machinery Rental Platform', margin, 18);
  doc.text(COMPANY.web, margin, 23);

  y = 36;

  // Title
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(10, 10, 10);
  const title = user.role === 'owner'
    ? 'OWNER PARTNER AGREEMENT'
    : 'CLIENT SERVICE AGREEMENT';
  doc.text(title, W / 2, y, { align: 'center' });
  y += 8;

  // Date + Agreement No
  const today = fmtDate(new Date().toISOString());
  const agreeNo = `DE/AGR/${new Date().getFullYear()}/${String(user.id || '').slice(-6).toUpperCase()}`;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  doc.text(`Agreement No: ${agreeNo}`, margin, y);
  doc.text(`Date: ${today}`, W - margin, y, { align: 'right' });
  y += 6;

  // Divider
  doc.setDrawColor(...rgb(BRAND.gold));
  doc.setLineWidth(0.5);
  doc.line(margin, y, W - margin, y);
  y += 8;

  // Party details
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(10, 10, 10);
  doc.text('PARTIES', margin, y);
  y += 6;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(50, 50, 50);

  // Platform party
  doc.text('Platform:', margin, y);
  doc.setFont('helvetica', 'bold');
  doc.text('Development Express', margin + 22, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.text(`Address: ${COMPANY.addr}`, margin, y);
  y += 5;
  doc.text(`Contact: ${COMPANY.phone} | ${COMPANY.email}`, margin, y);
  y += 5;
  doc.text(`GSTIN: ${displayGstin(COMPANY.gstin)}`, margin, y);
  y += 8;

  // User party
  doc.text(user.role === 'owner' ? 'Owner:' : 'Client:', margin, y);
  doc.setFont('helvetica', 'bold');
  doc.text(user.company || user.name || '-', margin + 22, y);
  y += 5;
  doc.setFont('helvetica', 'normal');
  doc.text(`Phone: ${user.phone || '-'}`, margin, y);
  y += 5;
  doc.text(`GSTIN: ${displayGstin(user.gstin)}`, margin, y);
  y += 8;

  // Divider
  doc.line(margin, y, W - margin, y);
  y += 8;

  // Role-specific terms
  const terms = user.role === 'owner'
    ? getOwnerTerms()
    : getClientTerms();

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(10, 10, 10);
  doc.text('TERMS & CONDITIONS', margin, y);
  y += 6;

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(50, 50, 50);

  for (const term of terms) {
    // Check page space
    if (y > 260) {
      doc.addPage();
      y = 20;
    }
    doc.setFont('helvetica', 'bold');
    doc.text(term.title, margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    const lines = doc.splitTextToSize(
      term.content, contentW,
    );
    doc.text(lines, margin, y);
    y += lines.length * 4.5 + 4;
  }

  // Signature section
  if (y > 240) { doc.addPage(); y = 20; }
  y += 4;
  doc.line(margin, y, W - margin, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('DIGITAL ACCEPTANCE', margin, y);
  y += 6;

  doc.setFontSize(8.5);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `By registering on Development Express platform, ${user.name || 'User'} `
    + 'agrees to all terms above.',
    margin, y, { maxWidth: contentW },
  );
  y += 10;

  doc.text(`Accepted by: ${user.name || '-'}`, margin, y);
  y += 5;
  doc.text(`Phone: ${user.phone || '-'}`, margin, y);
  y += 5;
  doc.text(`Date: ${today}`, margin, y);
  y += 5;
  doc.text(`Agreement No: ${agreeNo}`, margin, y);

  // Footer
  doc.setFontSize(7);
  doc.setTextColor(150, 150, 150);
  doc.text(
    `Development Express | ${COMPANY.addr} | ${COMPANY.phone}`,
    W / 2, 290, { align: 'center' },
  );

  doc.save(`DE-Agreement-${user.role}-${agreeNo}.pdf`);
}

function getClientTerms() {
  return [
    {
      title: '1. Platform Service',
      content: 'Development Express provides a digital platform connecting construction machinery owners with clients. The platform facilitates booking, payment, and monitoring services.',
    },
    {
      title: '2. Booking & Payment',
      content: 'Client must pay 50% advance at booking. Balance is due after work completion and confirmation. All payments are processed through the platform wallet. GST @ 18% applicable on all transactions.',
    },
    {
      title: '3. Cancellation Policy',
      content: 'Cancellation before dispatch: Full advance refund. After dispatch: No refund on advance. Disputes regarding cancellation are subject to platform Dispute Resolution policy.',
    },
    {
      title: '4. Work Hours & Billing',
      content: 'Billing is based on actual hours logged by the operator. Minimum hours as set by the machine owner apply. Extensions must be pre-approved and paid via wallet.',
    },
    {
      title: '5. Dispute Resolution',
      content: 'Disputes must be raised within 24 hours of booking completion (spot) or within 15 days of RA-Bill generation (contract). Disputes are resolved through platform grievance process, followed by conciliation and arbitration as per Arbitration and Conciliation Act 1996. Seat: Karad, Satara, Maharashtra.',
    },
    {
      title: '6. Data & Privacy',
      content: 'Client data is stored securely and used only for platform services. Data is not sold to third parties. Refer to Privacy Policy at developmentexpress.in.',
    },
    {
      title: '7. Force Majeure',
      content: 'Force Majeure — Neither party shall be liable for delays or non-performance due to events beyond reasonable control including natural disasters, floods, monsoon, government restrictions, or acts of God. Client will be notified within 48 hours of any force majeure event affecting the booking. This clause operates as per Section 56 of the Indian Contract Act 1872.',
    },
    {
      title: '8. Governing Law',
      content: 'This agreement is governed by the laws of India. Jurisdiction: Karad, Satara, Maharashtra.',
    },
  ];
}

function getOwnerTerms() {
  return [
    {
      title: '1. Platform Partnership',
      content: 'Owner registers machines on Development Express platform and receives bookings through the platform. Owner is responsible for machine maintenance, operator assignment, and service quality.',
    },
    {
      title: '2. Commission & Settlement',
      content: 'Platform commission: 15% of gross booking amount (spot bookings) / 18% of gross booking amount (contract bookings). GST-TCS: 1% as per GST Act Section 52. Settlement is credited to owner wallet after client confirmation. Bank payout requires admin approval with UTR.',
    },
    {
      title: '3. TDS Applicability',
      content: 'TDS under Income-tax Act 1961 is applicable on platform settlements as per prevailing rates. TDS section applicability (194C or 194-O) is subject to CA confirmation. Owner is responsible for TDS compliance filing and furnishing valid PAN. Failure to furnish PAN may attract higher TDS deduction as per Section 206AA.',
    },
    {
      title: '4. Operator Responsibility',
      content: 'Owner is responsible for operator background verification, skill certification, and conduct. Owner must ensure operator has valid license and experience documents.',
    },
    {
      title: '5. Machine Standards',
      content: 'Machine must have valid insurance, pollution certificate, and RC. Owner must maintain machine in working condition. Platform reserves right to delist underperforming machines.',
    },
    {
      title: '6. Dispute & Liability',
      content: 'Owner is liable for operator misconduct, machine breakdown during booking, and safety incidents. Platform is not liable for on-site accidents or third-party damage.',
    },
    {
      title: '7. Force Majeure',
      content: 'Force Majeure — Neither party shall be liable for failure to perform obligations due to events beyond reasonable control including natural disasters, floods, monsoon, government orders, pandemic, machine breakdown due to manufacturer defect, or acts of God. The affected party must notify within 48 hours. Platform may place booking on hold during force majeure period. This clause operates as per Section 56 of the Indian Contract Act 1872.',
    },
    {
      title: '8. Payment Discipline',
      content: 'Payment Discipline — Where the owner is a registered MSME supplier (Udyam registered), platform shall ensure settlement within 45 days of RA-Bill acceptance as per MSMED Act 2006 Section 15. Delayed settlement beyond 45 days shall attract compound interest at three times RBI bank rate as per Section 16. Interest on delayed payment is not tax-deductible for the payer as per Section 23.',
    },
    {
      title: '9. IoT Device & Hardware Ownership',
      content: 'Development Express installs GPS tracking and monitoring devices on owner machines for platform operations. All such hardware remains the exclusive property of Development Express at all times (Serial No. recorded at installation). The device shall remain on the machine while the owner: (a) complies with all platform terms and conditions, (b) pays platform commission and fees on time, and (c) does not tamper with or interfere with the device. Owner responsibilities: physical safety and security of the installed device; report any damage or theft to Development Express within 24 hours; allow Development Express technician access for maintenance or removal. Owner liability (excluding manufacturing defects): device damage or loss — full replacement cost as per Development Express invoice; device theft — full replacement cost plus FIR copy required within 48 hours; tampering or unauthorized removal — immediate account suspension plus penalty of ₹10,000. Manufacturing defects: Development Express will file warranty claim with manufacturer at no cost to owner. Unauthorized removal or tampering is strictly prohibited.',
    },
    {
      title: '10. Dispute Resolution',
      content: 'Dispute Resolution — Any dispute arising out of this agreement shall first be resolved through conciliation as per Arbitration and Conciliation Act 1996 (Part III). If unresolved within 90 days, dispute shall be referred to arbitration under Arbitration and Conciliation Act 1996 (Part I). Seat of arbitration: Karad, Satara, Maharashtra. Single arbitrator mutually appointed. Award shall be final and binding. Courts at Karad, Satara shall have jurisdiction only for interim relief under Section 9 or enforcement under Section 36 of the Act.',
    },
    {
      title: '11. Governing Law',
      content: 'This agreement is governed by the laws of India. Jurisdiction: Karad, Satara, Maharashtra.',
    },
  ];
}
